/* import __COLOCATED_TEMPLATE__ from './messenger-preview.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import type AppService from 'embercom/services/app-service';
import type IntlService from 'embercom/services/intl';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { debounce, later } from '@ember/runloop';
import { modifier } from 'ember-modifier';

type VisibilityMode = 'shrinked' | 'collapsed' | 'visible';

interface Suggestion {
  id: number;
  text: string;
  display_priority: number;
}

interface Signature {
  Args: {
    glowColor: string | null;
    verticalPadding: number | null;
    openSectionId?: string;
    suggestions?: Suggestion[];
    smartSuggestionsEnabled?: boolean;
    showWebInstallationBanner?: boolean;
  };
  Element: HTMLDivElement;
}

export default class SalesMessengerPreview extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  @tracked selectedMode: VisibilityMode = 'shrinked';
  @tracked isLoading = true;

  private iframeElement: HTMLIFrameElement | null = null;
  private containerElement: Element | null = null;

  get appId() {
    return this.appService.app.id;
  }

  @action
  onGlowColorChanged() {
    debounce(this, this.sendGlowColorUpdate, 300);
  }

  @action
  onVerticalPaddingChanged() {
    debounce(this, this.sendPreviewUpdate, 300);
  }

  private sendGlowColorUpdate() {
    this.sendPreviewUpdate();
  }

  private sendPreviewUpdate() {
    this.sendMessageToIframe({
      type: 'sales-preview-update',
      glow_color: this.args.glowColor,
      action_color: this.args.glowColor,
      vertical_padding: this.args.verticalPadding,
    });
  }

  @action
  onSuggestionsChanged() {
    this.sendSuggestionsUpdate();
  }

  private sendSuggestionsUpdate() {
    let suggestionsEnabled = this.args.smartSuggestionsEnabled ?? false;
    let topThreeTexts = suggestionsEnabled
      ? (this.args.suggestions ?? []).slice(0, 3).map((s) => s.text)
      : [];
    this.sendMessageToIframe({
      type: 'sales-preview-update',
      smart_suggestions_enabled: suggestionsEnabled,
      suggestions: topThreeTexts,
    });
  }

  @action
  onSectionChanged() {
    if (this.args.openSectionId === 'appearance') {
      this.setPreviewMode('shrinked');
    } else if (this.args.openSectionId === 'smart-suggestions') {
      this.setPreviewMode('collapsed');
    }
  }

  @action
  onIframeLoaded() {
    this.isLoading = false;
    this.applyThemeBackground();
    // Delay initial state to ensure iframe's postMessage listener is ready
    later(this, this.sendInitialState, 500);
  }

  private applyThemeBackground() {
    if (!this.iframeElement?.contentDocument || !this.containerElement) {
      return;
    }
    let bgColor = getComputedStyle(this.containerElement).backgroundColor;
    this.iframeElement.contentDocument.documentElement.style.background = bgColor;
    if (this.iframeElement.contentDocument.body) {
      this.iframeElement.contentDocument.body.style.background = bgColor;
    }
  }

  private sendInitialState() {
    this.sendMessageToIframe({
      type: 'sales-preview-set-visibility',
      state: this.selectedMode,
    });
    this.sendPreviewUpdate();
    this.sendSuggestionsUpdate();
    // Re-apply bg after hosted messenger has likely finished rendering
    this.applyThemeBackground();
    // Safety net for slower loads
    later(this, this.applyThemeBackground, 1000);
  }

  @action
  setPreviewMode(mode: VisibilityMode) {
    this.selectedMode = mode;
    this.sendMessageToIframe({
      type: 'sales-preview-set-visibility',
      state: mode,
    });
    this.sendPreviewUpdate();
    this.sendSuggestionsUpdate();
  }

  private sendMessageToIframe(message: Record<string, unknown>) {
    if (this.iframeElement?.contentWindow) {
      this.iframeElement.contentWindow.postMessage(JSON.stringify(message), window.location.origin);
    }
  }

  private static MIN_SUITABLE_HEIGHT = 200;

  private createIframe(el: HTMLElement) {
    if (this.iframeElement) {
      this.iframeElement.removeEventListener('load', this.onIframeLoaded);
    }
    this.isLoading = true;

    let iframe = document.createElement('iframe');
    iframe.addEventListener('load', this.onIframeLoaded);

    let iframeUrl = `${window.location.origin}/hosted_messenger/${this.appId}/sales_demo_preview?preview_only=true`;
    iframeUrl += `&language_override=${this.intl.primaryLocale}`;
    iframe.src = iframeUrl;
    iframe.setAttribute('id', 'sales-messenger-preview-iframe');
    iframe.style.cssText =
      'width: 100%; height: 100%; max-width: 100%; max-height: 100%; border: none;';

    el.replaceChildren(iframe);
    this.iframeElement = iframe;
    this.containerElement = el.closest('[data-test-sales-messenger-preview]');
  }

  // Creates the iframe and observes the container's height via ResizeObserver.
  // The hosted messenger evaluates its viewport on initial load to decide
  // between desktop and mobile layout, but does not re-evaluate on resize.
  // When the container height drops below MIN_SUITABLE_HEIGHT (e.g. the panel
  // is hidden by a CSS media query) and later becomes tall enough again, we
  // recreate the iframe so the messenger re-runs its viewport check and
  // renders the correct layout. The recreation is debounced to avoid
  // repeated iframe reloads during continuous window resizing.
  // See also: operator/fin/demo-preview.ts (same bindIFrame + hosted messenger iframe pattern)
  bindIFrame = modifier((el: HTMLElement) => {
    this.createIframe(el);

    let wasSuitable = false;
    let initialMount = true;

    let observer = new ResizeObserver((entries) => {
      let height = entries[0]?.contentRect.height ?? 0;
      let isSuitable = height >= SalesMessengerPreview.MIN_SUITABLE_HEIGHT;

      if (initialMount) {
        initialMount = false;
        wasSuitable = isSuitable;
        return;
      }

      if (isSuitable && !wasSuitable) {
        debounce(this, this.createIframe, el, 300);
      }
      wasSuitable = isSuitable;
    });

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (this.iframeElement) {
        this.iframeElement.removeEventListener('load', this.onIframeLoaded);
      }
      this.iframeElement = null;
      this.containerElement = null;
    };
  });
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'MessengerSettings::Sales::MessengerPreview': typeof SalesMessengerPreview;
    'messenger-settings/sales/messenger-preview': typeof SalesMessengerPreview;
  }
}
